import { useCallback, useEffect, useState } from 'react';
import type { RangeMode, SummaryView } from '../lib/reports';
import { reportsApi } from '../lib/reports';
import { ApiError } from '../lib/api';
import { openPrint } from '../lib/print';
import { formatDate, formatLitres, formatRs } from '../lib/money';
import CostChart from '../components/CostChart';
import CostCodePie, { share } from '../components/CostCodePie';
import type { CostCode } from '../lib/costcodes';
import CostSummaryReport, { PlantCostReport } from '../print/CostSummary';

/**
 * The owner's Summary.
 *
 * "Period chips — Today, Last 7 days, Last 30 days, This month, Last month —
 * anchored to the real date and independent of day navigation, plus From/To
 * boxes which hide when Today is selected. Metrics, a stacked bar chart of
 * labour/diesel/misc per day, a day-by-day table with cost and cash-out
 * columns, and a cost by plant section with share bars and an expandable
 * per-machine breakdown including the misc booked to it."
 *
 * The chips are resolved on the SERVER against the business date, so they mean
 * the same thing on the owner's phone as on the office tablet.
 */

const CHIPS: { mode: RangeMode; label: string }[] = [
  { mode: 'day', label: 'Today' },
  { mode: 'week', label: 'Last 7 days' },
  { mode: 'month', label: 'Last 30 days' },
  { mode: 'cal', label: 'This month' },
  { mode: 'prev', label: 'Last month' },
];

export default function Summary({ userName, costCodes }: { userName: string; costCodes: CostCode[] }) {
  const [mode, setMode] = useState<RangeMode | null>('week');
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [view, setView] = useState<SummaryView | null>(null);
  const [openPlant, setOpenPlant] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await reportsApi.summary(
        mode
          ? { mode }
          : range
            ? { from: range.from, to: range.to }
            : { mode: 'week' },
      );
      setView(result);
      setRange({ from: result.from, to: result.to });
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the summary.');
    }
    // `range` is deliberately not a dependency: typing in the date boxes sets
    // it, and the effect re-runs from `mode` going null.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, range?.from, range?.to]);

  useEffect(() => { void load(); }, [load]);

  if (!view) return <div className="empty">{error || 'Loading…'}</div>;

  const totals = view.totals;
  const plantGrand = view.plants.reduce((sum, plant) => sum + plant.total_rs, 0);
  const codeGrand = (view.costCodes ?? []).reduce((sum, row) => sum + row.total_rs, 0);

  return (
    <>
      <div className="bar" style={{ margin: '12px 0 8px' }}>
        {CHIPS.map((chip) => (
          <button
            key={chip.mode}
            type="button"
            className={`btn sm${mode === chip.mode ? ' pri' : ''}`}
            style={{ minWidth: 0 }}
            onClick={() => setMode(chip.mode)}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="sub" style={{ margin: '-2px 0 8px' }}>
        {formatDate(view.from)} – {formatDate(view.to)}
      </div>

      {/* The boxes hide when Today is selected — there is nothing to pick. */}
      {mode !== 'day' && (
        <div className="bar" style={{ marginBottom: 10 }}>
          <div>
            <div className="sub">From</div>
            <input
              type="date"
              value={view.from}
              onChange={(e) => { setMode(null); setRange({ from: e.target.value, to: view.to }); }}
            />
          </div>
          <div>
            <div className="sub">To</div>
            <input
              type="date"
              value={view.to}
              onChange={(e) => { setMode(null); setRange({ from: view.from, to: e.target.value }); }}
            />
          </div>
        </div>
      )}

      {error && <div className="banner b-no">{error}</div>}

      <div className="grid2">
        <div className="metric">
          <div className="k">Total cost</div>
          <div className="v">{formatRs(totals.costRs)}</div>
          <div className="k">{totals.workingDays} working days</div>
        </div>
        <div className="metric">
          <div className="k">Cash paid out</div>
          <div className="v">{formatRs(totals.cashOutRs)}</div>
          <div className="k">diesel + misc + advances</div>
        </div>
        <div className="metric">
          <div className="k">Labour</div>
          <div className="v">{formatRs(totals.labourRs)}</div>
          <div className="k">
            {totals.costRs ? Math.round((totals.labourRs / totals.costRs) * 100) : 0}% of cost
          </div>
        </div>
        <div className="metric">
          <div className="k">Diesel</div>
          <div className="v">{formatRs(totals.dieselRs)}</div>
          <div className="k">
            {totals.costRs ? Math.round((totals.dieselRs / totals.costRs) * 100) : 0}% of cost
          </div>
        </div>
      </div>

      {/* "an empty period says so rather than drawing an empty chart" */}
      {view.days.length === 0 ? (
        <div className="banner b-info">Nothing recorded in this period yet.</div>
      ) : (
        <div className="card">
          <h3>Cost by day</h3>
          <CostChart
            points={view.days.map((day) => ({
              date: day.work_date,
              labourRs: day.labour_rs,
              dieselRs: day.diesel_rs,
              miscRs: day.misc_rs,
            }))}
          />
        </div>
      )}

      {view.days.length > 0 && (
        <div className="card">
          <h3>Day by day</h3>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">Labour</th>
                  <th className="num">Diesel</th>
                  <th className="num">Misc</th>
                  <th className="num">Cost</th>
                  <th className="num">Cash out</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...view.days].reverse().map((day) => (
                  <tr key={day.work_date}>
                    <td>{formatDate(day.work_date)}</td>
                    <td className="num">{formatRs(day.labour_rs)}</td>
                    <td className="num">{formatRs(day.diesel_rs)}</td>
                    <td className="num">{formatRs(day.misc_rs)}</td>
                    <td className="num">{formatRs(day.cost_rs)}</td>
                    <td className="num">{formatRs(day.cash_out_rs)}</td>
                    <td>
                      {day.closed ? (
                        <span className="pill p-ok">Closed</span>
                      ) : (
                        <span className="pill p-wait">Open</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bar" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn"
              onClick={() =>
                openPrint(
                  `Cost summary — ${formatDate(view.from)} to ${formatDate(view.to)}`,
                  <CostSummaryReport view={view} printedBy={userName} printedAt={new Date()} />,
                )
              }
            >
              Print this period
            </button>
          </div>
        </div>
      )}

      {/* -------------------------------------------- cost by cost code */}
      {(view.costCodes?.length ?? 0) > 0 && (
        <div className="card">
          <h3>Cost by cost code</h3>
          <div className="sub">
            Labour earned, diesel and misc · {formatDate(view.from)} – {formatDate(view.to)}
          </div>
          <CostCodePie rows={view.costCodes ?? []} codes={costCodes} />

          {/* The table view: every code on its own line, including any the
              ring folds into "Other codes". */}
          <div className="scroll" style={{ marginTop: 12 }}>
            <table>
              <thead>
                <tr>
                  <th>Cost code</th>
                  <th className="num">Labour</th>
                  <th className="num">Diesel</th>
                  <th className="num">Misc</th>
                  <th className="num">Total</th>
                  <th className="num">Share</th>
                </tr>
              </thead>
              <tbody>
                {[...(view.costCodes ?? [])]
                  .sort((a, b) => (a.cost_code_id === '' ? 1 : b.cost_code_id === '' ? -1 : b.total_rs - a.total_rs))
                  .map((row) => (
                    <tr key={row.cost_code_id || 'none'}>
                      <td>
                        {row.cost_code_id ? (
                          <>
                            <span className="name">{row.code}</span>{' '}
                            <span className="sub">
                              {row.name}
                              {row.is_active === 0 && ' (removed)'}
                            </span>
                          </>
                        ) : (
                          <span className="sub">No cost code</span>
                        )}
                      </td>
                      <td className="num">{formatRs(row.labour_rs)}</td>
                      <td className="num">{formatRs(row.diesel_rs)}</td>
                      <td className="num">{formatRs(row.misc_rs)}</td>
                      <td className="num">{formatRs(row.total_rs)}</td>
                      <td className="num">{share(row.total_rs, codeGrand)}</td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr>
                  <th>Total</th>
                  <th className="num">{formatRs((view.costCodes ?? []).reduce((sum, row) => sum + row.labour_rs, 0))}</th>
                  <th className="num">{formatRs((view.costCodes ?? []).reduce((sum, row) => sum + row.diesel_rs, 0))}</th>
                  <th className="num">{formatRs((view.costCodes ?? []).reduce((sum, row) => sum + row.misc_rs, 0))}</th>
                  <th className="num">{formatRs(codeGrand)}</th>
                  <th className="num">100%</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------ cost by plant */}
      <div className="card">
        <h3>Cost by plant</h3>
        <div className="sub">
          Diesel plus every misc item booked to the machine · {formatDate(view.from)} –{' '}
          {formatDate(view.to)}
        </div>
      </div>

      <div className="grid2">
        <div className="metric">
          <div className="k">All plants</div>
          <div className="v">{formatRs(plantGrand)}</div>
        </div>
        <div className="metric">
          <div className="k">Dearest plant</div>
          <div className="v" style={{ fontSize: 18 }}>{view.plants[0]?.code ?? '—'}</div>
          <div className="k">{view.plants[0] ? formatRs(view.plants[0].total_rs) : ''}</div>
        </div>
      </div>

      <div className="card">
        {view.plants.length === 0 && <div className="empty">Nothing booked to a plant yet.</div>}
        {view.plants.map((plant) => {
          const share = plantGrand ? Math.round((plant.total_rs / plantGrand) * 100) : 0;
          return (
            <div key={plant.plant_id}>
              <div
                className="row tap"
                role="button"
                tabIndex={0}
                onClick={() => setOpenPlant(openPlant === plant.plant_id ? null : plant.plant_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setOpenPlant(openPlant === plant.plant_id ? null : plant.plant_id);
                  }
                }}
              >
                <div style={{ flex: 1 }}>
                  <div className="name">{plant.name}</div>
                  <div className="sub">
                    {plant.code} · {formatLitres(plant.litres_x100)} L diesel
                    {plant.misc_rs > 0 && ` · ${formatRs(plant.misc_rs)} other`}
                  </div>
                  {/* Share bar: the proportion at a glance. */}
                  <div className="sharebar">
                    <div style={{ width: `${share}%` }} />
                  </div>
                </div>
                <div className="stat">
                  <div className="name">{formatRs(plant.total_rs)}</div>
                  <div className="sub">{share}% of plant cost</div>
                </div>
              </div>
              {openPlant === plant.plant_id && (
                <PlantBreakdown
                  plantId={plant.plant_id}
                  from={view.from}
                  to={view.to}
                  onError={setError}
                />
              )}
            </div>
          );
        })}

        <div className="bar" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn"
            disabled={view.plants.length === 0}
            onClick={() =>
              openPrint(
                `Plant costs — ${formatDate(view.from)} to ${formatDate(view.to)}`,
                <PlantCostReport view={view} printedBy={userName} printedAt={new Date()} />,
              )
            }
          >
            Print plant cost report
          </button>
        </div>
      </div>
    </>
  );
}

function PlantBreakdown({
  plantId, from, to, onError,
}: {
  plantId: string;
  from: string;
  to: string;
  onError: (message: string) => void;
}) {
  const [data, setData] = useState<Awaited<ReturnType<typeof reportsApi.plantDetail>> | null>(null);

  useEffect(() => {
    let live = true;
    reportsApi
      .plantDetail(plantId, { from, to })
      .then((result) => { if (live) setData(result); })
      .catch((err: unknown) =>
        onError(err instanceof Error ? err.message : 'Could not load that plant.'),
      );
    return () => { live = false; };
  }, [plantId, from, to, onError]);

  if (!data) return <div className="empty">Loading…</div>;

  return (
    <div
      style={{
        background: 'var(--surface-2)', borderRadius: 12,
        padding: '10px 12px', margin: '0 -4px 8px',
      }}
    >
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th className="num">Litres</th>
              <th className="num">Diesel</th>
              <th className="num">Other</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.days.length === 0 && (
              <tr><td colSpan={5} className="empty">Nothing in this period.</td></tr>
            )}
            {data.days.map((day) => (
              <tr key={day.work_date}>
                <td>{formatDate(day.work_date)}</td>
                <td className="num">{formatLitres(day.litres_x100)}</td>
                <td className="num">{formatRs(day.diesel_rs)}</td>
                <td className="num">{day.misc_rs ? formatRs(day.misc_rs) : '—'}</td>
                <td className="num">{formatRs(day.total_rs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.miscItems.length > 0 && (
        <>
          <div className="sub" style={{ marginTop: 10 }}>Other costs booked to this plant</div>
          {data.miscItems.map((item, index) => (
            <div className="ln" key={`${item.work_date}-${index}`}>
              <span className="k">
                {formatDate(item.work_date)} · {item.description}
              </span>
              <span>{formatRs(item.amount_rs)}</span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
